import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  CalendarDays,
  Hourglass,
  Video,
  Clock3,
  ArrowRight,
  ChevronDown,
  MessageCircle,
  Phone,
  Mail,
  Globe,
  User,
  Mic,
} from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { CONTACT, IMAGES, waLink } from "@/config";
import { COURSE, CONSULT, SINASTRI, COCUK, ONGORU, ISIM, BEBEK, ORTAKLIK, NEKSUS, ATOLYELER } from "@/data/course";

const NAVY = "#0B1325";
const CREAM = "#FDFBF7";

const FACT_ICONS = { calendar: CalendarDays, hourglass: Hourglass, video: Video, clock: Clock3 };

const GoldDot = ({ mark = "✦" }) => <span className="text-gold mr-2.5 select-none">{mark}</span>;

const WaButton = ({ message, children, testId, className = "" }) => (
  <a
    href={waLink(message)}
    target="_blank"
    rel="noopener noreferrer"
    data-testid={testId}
    className={`group inline-flex items-center justify-center gap-2.5 whitespace-nowrap rounded-full bg-gold px-7 py-3.5 text-sm font-semibold text-ink transition-all duration-300 hover:bg-gold-light hover:shadow-[0_0_28px_rgba(184,150,42,0.45)] ${className}`}
  >
    {children}
    <MessageCircle className="w-4 h-4" />
  </a>
);

const EduHeader = () => (
  <header
    data-testid="edu-header"
    className="fixed top-0 inset-x-0 z-50 bg-navy/90 backdrop-blur-xl border-b border-gold/25"
  >
    <div className="mx-auto max-w-6xl px-5 sm:px-8 py-3.5 flex items-center justify-between gap-4">
      <a href="#top" data-testid="edu-logo" className="flex items-center gap-3">
        <img src={IMAGES.logoN} alt="Neksus" className="h-10 w-10 object-contain drop-shadow-[0_0_12px_rgba(184,150,42,0.5)]" />
        <span className="font-serif text-base sm:text-lg tracking-[0.2em] text-gold">
          NEKSUS <span className="text-cream/80">AKADEMİ</span>
        </span>
      </a>
    </div>
  </header>
);

const HeroBlock = () => {
  const h = COURSE.hero;
  const [open, setOpen] = useState(false);
  return (
    <section id="top" data-testid="edu-hero" className="py-4 sm:py-6">
      <div className="mx-auto max-w-6xl px-4 sm:px-8">
        <Reveal>
          <button
            onClick={() => setOpen(!open)}
            data-testid="edu-banner-toggle"
            aria-expanded={open}
            className="group block w-full overflow-hidden rounded-3xl border border-gold/40 shadow-[0_40px_90px_-30px_rgba(11,19,37,0.35)] transition-shadow duration-500 hover:shadow-[0_50px_110px_-30px_rgba(184,150,42,0.4)]"
          >
            <img
              src={IMAGES.eduBanner}
              alt="Temel Orta İleri Numeroloji Eğitimi — 16 Haftalık Kapsamlı Uzmanlaşma Programı, 5 Kasım 2026"
              data-testid="edu-hero-banner"
              className="w-full object-cover transition-transform duration-700 group-hover:scale-[1.015]"
            />
          </button>
          <h1 className="sr-only">
            {h.title} — {h.subtitle}
          </h1>
        </Reveal>

        <Reveal delay={0.15}>
          <div className="mt-5 text-center">
            <button
              onClick={() => setOpen(!open)}
              data-testid="edu-info-toggle"
              aria-expanded={open}
              className="group inline-flex items-center gap-3 rounded-full bg-gold px-10 py-4 text-base font-semibold text-ink transition-all duration-300 hover:bg-gold-light hover:shadow-[0_0_35px_rgba(184,150,42,0.5)]"
            >
              Eğitimi İncele
              <motion.span animate={{ rotate: open ? 180 : 0 }} transition={{ duration: 0.4 }}>
                <ChevronDown className="w-5 h-5" />
              </motion.span>
            </button>
          </div>
        </Reveal>

        <AnimatePresence initial={false}>
          {open && (
            <motion.div
              key="edu-details"
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
              className="overflow-hidden"
              data-testid="edu-hero-details"
            >
              <div
                className="mt-7 rounded-3xl border border-gold/40 bg-navy-800 px-6 sm:px-12 py-10 text-center"
              >
                <p className="font-serif text-lg sm:text-xl text-gold">
                  {h.subtitle}
                </p>
                <p className="mt-5 mx-auto max-w-3xl text-sm sm:text-base leading-relaxed text-mist">
                  {h.description}
                </p>
                <div className="mt-8 grid grid-cols-2 lg:grid-cols-4 gap-3 max-w-3xl mx-auto">
                  {h.facts.map((f) => {
                    const Icon = FACT_ICONS[f.icon];
                    return (
                      <div
                        key={f.label}
                        data-testid={`edu-fact-${f.icon}`}
                        className="flex flex-col items-center gap-2 rounded-xl border border-gold/35 bg-navy px-3 py-4"
                      >
                        <Icon className="w-5 h-5 text-gold" />
                        <span className="text-xs sm:text-sm text-center text-cream">
                          {f.label}
                        </span>
                      </div>
                    );
                  })}
                </div>
                <div className="mt-9 inline-flex flex-col sm:flex-row items-center gap-5 rounded-2xl border border-gold/40 bg-navy px-8 py-6">
                  <div className="text-center sm:text-left">
                    <div className="font-serif text-2xl sm:text-3xl text-gold" data-testid="edu-early-price">
                      {h.earlyNote}
                    </div>
                  </div>
                  <a
                    href="#ucretler"
                    data-testid="edu-hero-cta"
                    className="group inline-flex items-center gap-3 rounded-full bg-gold px-8 py-4 text-sm font-semibold text-ink transition-all duration-300 hover:bg-gold-light hover:shadow-[0_0_30px_rgba(212,175,55,0.5)]"
                  >
                    Eğitime Katıl
                    <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1.5" />
                  </a>
                </div>

                <div className="mt-10 border-t border-gold/20 pt-8 text-left" data-testid="edu-hero-curriculum">
                  <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-gold">
                    Eğitim Programı · Müfredat
                  </p>
                  <div className="mt-5 space-y-6">
                    {COURSE.curriculum.map((w, i) => (
                      <div key={w.week} data-testid={`hero-week-${i + 1}`} className="border-l border-gold/30 pl-5">
                        <div className="flex flex-wrap items-baseline gap-x-3">
                          <span className="font-mono text-[11px] tracking-[0.2em] text-gold uppercase">{w.week}</span>
                          <h4 className="font-serif text-lg sm:text-xl text-cream">
                            {w.title}
                          </h4>
                        </div>
                        {w.bullets && (
                          <ul className="mt-2 space-y-1.5">
                            {w.bullets.map((b) => (
                              <li key={b} className="text-xs sm:text-sm leading-relaxed text-mist">
                                <GoldDot mark="✧" />
                                {b}
                              </li>
                            ))}
                          </ul>
                        )}
                        <div className="mt-2 space-y-2">
                          {w.blocks.map((p, j) => (
                            <p key={j} className="text-xs sm:text-sm leading-relaxed text-mist">
                              <GoldDot mark={j % 2 ? "✧" : "✦"} />
                              {p}
                            </p>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="mt-10 border-t border-gold/20 pt-8 text-left" data-testid="edu-hero-expectations">
                  <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-gold">
                    Eğitim Boyunca Sizi Neler Bekliyor?
                  </p>
                  <div className="mt-5 grid sm:grid-cols-3 gap-4">
                    {COURSE.expectations.map((e) => (
                      <div key={e.title} className="rounded-xl border border-gold/30 bg-navy p-5">
                        <h4 className="font-serif text-base sm:text-lg text-cream">
                          {e.title}
                        </h4>
                        <p className="mt-2 text-xs leading-relaxed text-mist">
                          {e.text}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
};

const Pricing = () => (
  <section id="ucretler" data-testid="edu-pricing" className="py-10 sm:py-14 bg-navy-800/50">
    <div className="mx-auto max-w-6xl px-5 sm:px-8">
      <Reveal delay={0.1}>
        <p className="text-mist text-base md:text-lg max-w-2xl">
          Temelden ileri düzeye 16 haftalık Numeroloji Uzmanlaşma Programı · 5 Kasım 2026'da başlıyor.
        </p>
      </Reveal>
      <Reveal delay={0.2}>
        <div
          className="mt-10 rounded-2xl border border-gold/30 bg-navy/80 p-8 sm:p-12 text-center"
          data-testid="edu-register-card"
        >
          <h3 className="font-serif text-2xl sm:text-3xl text-cream">Kayıt & Detaylı Bilgi</h3>
          <p className="mt-4 mx-auto max-w-xl text-sm sm:text-base text-mist leading-relaxed">
            Kayıt ve detaylı bilgi için bize WhatsApp üzerinden yazın; sorularınızı yanıtlayalım
            ve kayıt sürecinizi birlikte planlayalım.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-4">
            <a
              href={waLink("Merhaba, Numeroloji Eğitimi kaydı ve detaylı bilgi almak istiyorum.")}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="edu-register-wa"
              className="group inline-flex items-center gap-2.5 rounded-full bg-gold px-8 py-4 text-sm font-semibold text-ink transition-all duration-300 hover:bg-gold-light hover:shadow-[0_0_30px_rgba(184,150,42,0.5)]"
            >
              <MessageCircle className="w-5 h-5" />
              WhatsApp'tan Yazın
            </a>
            <a
              href={`tel:${CONTACT.whatsappNumber}`}
              data-testid="edu-register-phone"
              className="inline-flex items-center gap-2.5 rounded-full border border-gold/50 px-7 py-3.5 text-sm font-semibold text-cream transition-all duration-300 hover:bg-gold/10"
            >
              <Phone className="w-4 h-4 text-gold" />
              {CONTACT.whatsappDisplay}
            </a>
          </div>
        </div>
      </Reveal>
    </div>
  </section>
);

const ContactBlock = () => (
  <section data-testid="edu-contact" className="py-12 sm:py-16">
    <div className="mx-auto max-w-6xl px-5 sm:px-8">
      <Reveal>
        <div className="rounded-3xl border border-gold/40 p-8 sm:p-14 text-center" style={{ background: NAVY }}>
          <h2 className="font-serif text-2xl sm:text-3xl" style={{ color: CREAM }}>
            Bilgi & İletişim
          </h2>
          <p className="mt-4 mx-auto max-w-2xl text-sm sm:text-base leading-relaxed" style={{ color: "#A8B4CE" }}>
            Numeroloji eğitimi hakkında detaylı bilgi almak, eğitim süreci ve kayıt koşullarını öğrenmek
            veya aklınızdaki sorulara cevap almak için bizimle iletişime geçebilirsiniz.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-4">
            <WaButton message="Merhaba, Numeroloji Eğitimi hakkında bilgi almak istiyorum." testId="edu-contact-wa">
              WhatsApp'tan Yazın
            </WaButton>
            <a
              href={`tel:${CONTACT.whatsappNumber}`}
              data-testid="edu-contact-phone"
              className="inline-flex items-center gap-2.5 rounded-full border border-gold/50 px-7 py-3.5 text-sm font-semibold transition-all duration-300 hover:bg-gold/10"
              style={{ color: CREAM }}
            >
              <Phone className="w-4 h-4 text-gold" />
              {CONTACT.whatsappDisplay}
            </a>
            <a
              href={`mailto:${CONTACT.email}`}
              data-testid="edu-contact-email"
              className="inline-flex items-center gap-2.5 rounded-full border border-gold/50 px-7 py-3.5 text-sm font-semibold transition-all duration-300 hover:bg-gold/10"
              style={{ color: CREAM }}
            >
              <Mail className="w-4 h-4 text-gold" />
              {CONTACT.email}
            </a>
          </div>
          <div className="mt-10 border-t border-gold/20 pt-8">
            <p className="font-serif italic text-lg sm:text-xl text-gold">{COURSE.motto[0]}</p>
            <p className="mt-2 text-sm" style={{ color: "#A8B4CE" }}>
              ✦ Neksus Merkezi — {COURSE.motto[1]} ✦
            </p>
            <a
              href="/site"
              data-testid="edu-contact-site"
              className="mt-5 inline-flex items-center gap-2 text-sm text-gold link-underline"
            >
              <Globe className="w-4 h-4" />
              neksusmerkezi.com
            </a>
          </div>
        </div>
      </Reveal>
    </div>
  </section>
);

const CONSULT_ICONS = { user: User, video: Video, clock: Clock3, mic: Mic };

const NeksusBlock = ({ data, banner }) => {
  const [open, setOpen] = useState(false);
  return (
    <section data-testid="neksus-section" className="pt-24 sm:pt-28 pb-4 sm:pb-6">
      <div className="mx-auto max-w-6xl px-4 sm:px-8">
        <Reveal>
          <button
            onClick={() => setOpen(!open)}
            data-testid="neksus-banner-toggle"
            aria-expanded={open}
            className="group block w-full overflow-hidden rounded-3xl border border-gold/40 shadow-[0_40px_90px_-30px_rgba(11,19,37,0.35)] transition-shadow duration-500 hover:shadow-[0_50px_110px_-30px_rgba(184,150,42,0.4)]"
          >
            <img
              src={banner}
              alt="Neksus Nedir? — Nazife Cankaya Güder · Neksus"
              data-testid="neksus-banner"
              className="w-full object-cover transition-transform duration-700 group-hover:scale-[1.015]"
            />
          </button>
        </Reveal>
        <Reveal delay={0.15}>
          <div className="mt-5 text-center">
            <button
              onClick={() => setOpen(!open)}
              data-testid="neksus-info-toggle"
              aria-expanded={open}
              className="group inline-flex items-center gap-3 rounded-full bg-gold px-10 py-4 text-base font-semibold text-ink transition-all duration-300 hover:bg-gold-light hover:shadow-[0_0_35px_rgba(184,150,42,0.5)]"
            >
              Neksus'u Keşfet
              <motion.span animate={{ rotate: open ? 180 : 0 }} transition={{ duration: 0.4 }}>
                <ChevronDown className="w-5 h-5" />
              </motion.span>
            </button>
          </div>
        </Reveal>
        <AnimatePresence initial={false}>
          {open && (
            <motion.div
              key="neksus-details"
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
              className="overflow-hidden"
              data-testid="neksus-details"
            >
              <div className="mt-7 rounded-3xl border border-gold/40 bg-navy-800 px-6 sm:px-12 py-10 sm:py-12">
                <p className="font-mono text-[11px] sm:text-xs uppercase tracking-[0.3em] text-gold">
                  {data.eyebrow}
                </p>
                <h2
                  className="mt-4 font-serif text-3xl sm:text-4xl lg:text-5xl text-cream"
                  data-testid="neksus-title"
                >
                  {data.title}
                </h2>
                {data.sections.map((s, si) => (
                  <div key={si} className="mt-10 max-w-3xl" data-testid={`neksus-part-${si}`}>
                    <h3 className="font-serif text-xl sm:text-2xl text-gold">
                      <GoldDot mark={si % 2 ? "✧" : "✦"} />
                      {s.heading}
                    </h3>
                    <div className="mt-4 space-y-4">
                      {s.paragraphs.map((p, i) => (
                        <p key={i} className="text-sm sm:text-base leading-relaxed text-mist">
                          {p}
                        </p>
                      ))}
                    </div>
                    {s.items &&
                      s.items.map((it, ii) => (
                        <div key={ii} className="mt-6">
                          <h4 className="font-serif text-lg text-cream">
                            <GoldDot mark={ii % 2 ? "✧" : "✦"} />
                            {it.title}
                          </h4>
                          <div className="mt-3 space-y-3">
                            {it.paragraphs.map((p, i) => (
                              <p key={i} className="text-sm sm:text-base leading-relaxed text-mist">
                                {p}
                              </p>
                            ))}
                          </div>
                        </div>
                      ))}
                    {s.closingTitle && (
                      <h4 className="mt-8 font-serif text-lg text-cream">
                        <GoldDot mark="✧" />
                        {s.closingTitle}
                      </h4>
                    )}
                    {s.closingLines && (
                      <ul className="mt-3 space-y-2.5">
                        {s.closingLines.map((l) => (
                          <li key={l} className="text-sm sm:text-base leading-relaxed text-cream/90">
                            <GoldDot mark="✦" />
                            {l}
                          </li>
                        ))}
                      </ul>
                    )}
                    {s.closing && (
                      <div className="mt-4 space-y-4">
                        {s.closing.map((p, i) => (
                          <p
                            key={i}
                            className="text-sm sm:text-base leading-relaxed text-cream font-medium"
                          >
                            {p}
                          </p>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
};


const ServiceBlock = ({ data, banner, prefix }) => {
  const [open, setOpen] = useState(false);
  return (
    <section data-testid={`${prefix}-section`} className="py-4 sm:py-6">
      <div className="mx-auto max-w-6xl px-4 sm:px-8">
        <Reveal>
          <button
            onClick={() => setOpen(!open)}
            data-testid={`${prefix}-banner-toggle`}
            aria-expanded={open}
            className="group block w-full overflow-hidden rounded-3xl border border-gold/40 shadow-[0_40px_90px_-30px_rgba(11,19,37,0.35)] transition-shadow duration-500 hover:shadow-[0_50px_110px_-30px_rgba(184,150,42,0.4)]"
          >
            <img
              src={banner}
              alt={`${data.title} — Nazife Cankaya Güder · Neksus`}
              data-testid={`${prefix}-banner`}
              className="w-full object-cover transition-transform duration-700 group-hover:scale-[1.015]"
            />
          </button>
        </Reveal>
        <Reveal delay={0.15}>
          <div className="mt-5 text-center">
            <button
              onClick={() => setOpen(!open)}
              data-testid={`${prefix}-info-toggle`}
              aria-expanded={open}
              className="group inline-flex items-center gap-3 rounded-full bg-gold px-10 py-4 text-base font-semibold text-ink transition-all duration-300 hover:bg-gold-light hover:shadow-[0_0_35px_rgba(184,150,42,0.5)]"
            >
              Detayları Gör
              <motion.span animate={{ rotate: open ? 180 : 0 }} transition={{ duration: 0.4 }}>
                <ChevronDown className="w-5 h-5" />
              </motion.span>
            </button>
          </div>
        </Reveal>
        <AnimatePresence initial={false}>
          {open && (
            <motion.div
              key={`${prefix}-details`}
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
              className="overflow-hidden"
              data-testid={`${prefix}-details`}
            >
              <div className="mt-7 rounded-3xl border border-gold/40 bg-navy-800 px-6 sm:px-12 py-10 sm:py-12">
                <p className="font-mono text-[11px] sm:text-xs uppercase tracking-[0.3em] text-gold">
                  {data.eyebrow}
                </p>
                <h2
                  className="mt-4 font-serif text-3xl sm:text-4xl lg:text-5xl text-cream"
                  data-testid={`${prefix}-title`}
                >
                  {data.title}
                </h2>
                <div className="mt-6 max-w-3xl space-y-4">
                  {data.paragraphs.map((p, i) => (
                    <p key={i} className="text-sm sm:text-base leading-relaxed text-mist">
                      <GoldDot mark={i % 2 ? "✧" : "✦"} />
                      {p}
                    </p>
                  ))}
                </div>
                {data.bullets && (
                  <ul className="mt-4 max-w-3xl space-y-2.5">
                    {data.bullets.map((b) => (
                      <li key={b} className="text-sm sm:text-base leading-relaxed text-cream/90">
                        <GoldDot mark="✦" />
                        {b}
                      </li>
                    ))}
                  </ul>
                )}
                {data.closing && (
                  <div className="mt-4 max-w-3xl space-y-4">
                    {data.closing.map((p, i) => (
                      <p key={i} className="text-sm sm:text-base leading-relaxed text-mist">
                        <GoldDot mark={i % 2 ? "✧" : "✦"} />
                        {p}
                      </p>
                    ))}
                  </div>
                )}
                <div className="mt-8 grid sm:grid-cols-2 gap-3 max-w-3xl">
                  {data.facts.map((f) => {
                    const Icon = CONSULT_ICONS[f.icon];
                    return (
                      <div
                        key={f.label}
                        data-testid={`${prefix}-fact-${f.icon}`}
                        className="flex items-start gap-3 rounded-xl border border-gold/35 bg-navy px-4 py-4"
                      >
                        <Icon className="w-5 h-5 text-gold shrink-0 mt-0.5" />
                        <span className="text-xs sm:text-sm leading-relaxed text-cream">
                          {f.label}
                        </span>
                      </div>
                    );
                  })}
                </div>
                <div className="mt-9 rounded-2xl border border-gold/40 bg-navy px-6 sm:px-8 py-7" data-testid={`${prefix}-booking`}>
                  <h3 className="font-serif text-xl sm:text-2xl text-cream">
                    Randevu & Satın Alma
                  </h3>
                  <p className="mt-3 text-sm leading-relaxed text-mist">
                    {data.payNote}
                  </p>
                  <div className="mt-6 flex flex-wrap items-center gap-4">
                    {data.payOptions ? (
                      <div className="flex flex-wrap gap-3">
                        {data.payOptions.map((o) => (
                          <a
                            key={o.url}
                            href={o.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid={`${prefix}-pay-${o.label}`}
                            className="group inline-flex items-center gap-2.5 rounded-full bg-gold px-6 py-3.5 text-sm font-semibold text-ink transition-all duration-300 hover:bg-gold-light hover:shadow-[0_0_25px_rgba(212,175,55,0.45)]"
                          >
                            {o.label}
                            <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
                          </a>
                        ))}
                      </div>
                    ) : (
                      <a
                        href={data.payUrl || waLink(data.payWaMessage)}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`${prefix}-pay-cta`}
                        className="group inline-flex items-center gap-3 rounded-full bg-gold px-8 py-4 text-sm font-semibold text-ink transition-all duration-300 hover:bg-gold-light hover:shadow-[0_0_30px_rgba(212,175,55,0.5)]"
                      >
                        {data.payUrl ? "Güvenli Satın Al" : "Satın Alma Linki İçin Yazın"}
                        <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
                      </a>
                    )}
                    <a
                      href={waLink(data.bookingMessage)}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`${prefix}-wa-cta`}
                      className="inline-flex items-center gap-2.5 rounded-full border border-gold/50 px-7 py-3.5 text-sm font-semibold text-cream transition-all duration-300 hover:bg-gold/10"
                    >
                      <MessageCircle className="w-4 h-4 text-gold" />
                      WhatsApp'tan İletişim
                    </a>
                    <a
                      href={`tel:${CONTACT.whatsappNumber}`}
                      data-testid={`${prefix}-phone`}
                      className="inline-flex items-center gap-2.5 text-sm text-cream"
                    >
                      <Phone className="w-4 h-4 text-gold" />
                      {CONTACT.whatsappDisplay}
                    </a>
                  </div>
                  <p className="mt-5 text-xs leading-relaxed text-mist">
                    {data.transferNote}
                  </p>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
};

const AtolyelerBlock = ({ data, banner }) => {
  const [open, setOpen] = useState(false);
  return (
    <section data-testid="atolyeler-section" className="py-4 sm:py-6">
      <div className="mx-auto max-w-6xl px-4 sm:px-8">
        <Reveal>
          <button
            onClick={() => setOpen(!open)}
            data-testid="atolyeler-banner-toggle"
            aria-expanded={open}
            className="group block w-full overflow-hidden rounded-3xl border border-gold/40 shadow-[0_40px_90px_-30px_rgba(11,19,37,0.35)] transition-shadow duration-500 hover:shadow-[0_50px_110px_-30px_rgba(184,150,42,0.4)]"
          >
            <img
              src={banner}
              alt="Neksus Atölyeleri — Keşif, Derinleşme, Deneyim"
              data-testid="atolyeler-banner"
              className="w-full object-cover transition-transform duration-700 group-hover:scale-[1.015]"
            />
          </button>
        </Reveal>
        <Reveal delay={0.15}>
          <div className="mt-5 text-center">
            <button
              onClick={() => setOpen(!open)}
              data-testid="atolyeler-info-toggle"
              aria-expanded={open}
              className="group inline-flex items-center gap-3 rounded-full bg-gold px-10 py-4 text-base font-semibold text-ink transition-all duration-300 hover:bg-gold-light hover:shadow-[0_0_35px_rgba(184,150,42,0.5)]"
            >
              Atölyeleri Keşfet
              <motion.span animate={{ rotate: open ? 180 : 0 }} transition={{ duration: 0.4 }}>
                <ChevronDown className="w-5 h-5" />
              </motion.span>
            </button>
          </div>
        </Reveal>
        <AnimatePresence initial={false}>
          {open && (
            <motion.div
              key="atolyeler-details"
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
              className="overflow-hidden"
              data-testid="atolyeler-details"
            >
              <div className="mt-7 rounded-3xl border border-gold/40 bg-navy-800 px-6 sm:px-12 py-10 sm:py-12">
                <p className="font-mono text-[11px] sm:text-xs uppercase tracking-[0.3em] text-gold">
                  {data.eyebrow}
                </p>
                <h2
                  className="mt-4 font-serif text-3xl sm:text-4xl lg:text-5xl text-cream"
                  data-testid="atolyeler-title"
                >
                  {data.title}
                </h2>
                <div className="mt-6 space-y-4 max-w-3xl">
                  {data.paragraphs.map((p, i) => (
                    <p key={i} className="text-sm sm:text-base leading-relaxed text-mist">
                      {p}
                    </p>
                  ))}
                </div>
                <div className="mt-10 grid gap-6 md:grid-cols-2">
                  {data.groups.map((g, gi) => (
                    <div
                      key={gi}
                      className="rounded-2xl border border-gold/30 bg-navy-700/40 p-6 sm:p-8"
                      data-testid={`atolye-group-${gi}`}
                    >
                      <div className="flex items-center gap-3 flex-wrap">
                        <h3 className="font-serif text-xl sm:text-2xl text-gold">
                          <GoldDot mark={gi % 2 ? "✧" : "✦"} />
                          {g.heading}
                        </h3>
                        {g.tag && (
                          <span className="rounded-full bg-gold px-3 py-1 text-xs font-semibold text-ink">
                            {g.tag}
                          </span>
                        )}
                      </div>
                      <div className="mt-4 space-y-3">
                        {g.paragraphs.map((p, i) => (
                          <p key={i} className="text-sm leading-relaxed text-mist">
                            {p}
                          </p>
                        ))}
                      </div>
                      {g.workshop && (
                        <div className="mt-6 rounded-xl border border-gold/40 bg-navy-800/70 p-5 sm:p-6">
                          <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-gold">
                            {g.workshop.label}
                          </p>
                          <h4 className="mt-2 font-serif text-lg sm:text-xl text-cream">
                            {g.workshop.title}
                          </h4>
                          <div className="mt-3 space-y-3">
                            {g.workshop.paragraphs.map((p, i) => (
                              <p key={i} className="text-sm leading-relaxed text-mist">
                                {p}
                              </p>
                            ))}
                          </div>
                        </div>
                      )}
                      <div className="mt-6">
                        <a
                          href={waLink(g.joinMessage)}
                          target="_blank"
                          rel="noopener noreferrer"
                          data-testid={`atolye-join-${gi}`}
                          className="inline-flex items-center gap-2.5 rounded-full border border-gold px-6 py-3 text-sm font-semibold text-gold transition-all duration-300 hover:bg-gold hover:text-ink"
                        >
                          <MessageCircle className="w-4 h-4" />
                          Katılım İçin Yazın
                        </a>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
};


export default function EducationPage() {
  return (
    <div data-testid="education-page">
      <EduHeader />
      <main>
        <NeksusBlock data={NEKSUS} banner={IMAGES.neksusBanner} />
        <HeroBlock />
        <Pricing />
        <ServiceBlock data={CONSULT} banner={IMAGES.consultBanner} prefix="consult" />
        <AtolyelerBlock data={ATOLYELER} banner={IMAGES.atolyeBanner} />
        <ServiceBlock data={SINASTRI} banner={IMAGES.sinastriBanner} prefix="sinastri" />
        <ServiceBlock data={COCUK} banner={IMAGES.cocukBanner} prefix="cocuk" />
        <ServiceBlock data={ONGORU} banner={IMAGES.ongoruBanner} prefix="ongoru" />
        <ServiceBlock data={ISIM} banner={IMAGES.isimBanner} prefix="isim" />
        <ServiceBlock data={BEBEK} banner={IMAGES.bebekBanner} prefix="bebek" />
        <ServiceBlock data={ORTAKLIK} banner={IMAGES.ortaklikBanner} prefix="ortaklik" />
        <ContactBlock />
      </main>
      <p className="pb-8 text-center text-xs text-mist/70">© 2026 Neksus. Tüm Hakları Saklıdır.</p>
    </div>
  );
}
